/**
 * M List page. Frontend-only filtering: value, badge, philanthropy, category/subcategory.
 * Data from hoodieApi (listings, tag-groupings). No backend changes.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getTagGroupings, getListings, isHoodieApiConfigured } from "../../../api";
import {
  MListHeroIntroSection,
  MListFilterBar,
  MListSidebar,
  MListResultsSection,
  applyListingsFilters,
  buildTagIdMap,
} from "../mlist";
import "../styles/TwentyTwentysixContent.css";

const FALLBACK_CATEGORIES = [
  { id: "fallback-1", title: "New Moms", name: "New Moms", tags: [] },
  { id: "fallback-2", title: "Health & Wellness", name: "Health & Wellness", tags: [] },
  { id: "fallback-3", title: "Money & Career", name: "Money & Career", tags: [] },
];

const LISTINGS_PAGE_SIZE = 500;

function normalizeGroupings(data) {
  if (!Array.isArray(data) || data.length === 0) return null;
  return data.map((g) => ({
    id: g.id,
    name: g.name || g.slug || "Category",
    slug: g.slug,
    tags: Array.isArray(g.tags) ? g.tags.map((t) => ({ id: t.id, name: t.name || t.slug || "", slug: t.slug })) : [],
  }));
}

export default function MListPage() {
  const [groupings, setGroupings] = useState(null);
  const [allListings, setAllListings] = useState([]);
  const [nextOffset, setNextOffset] = useState(null);
  const [loading, setLoading] = useState(isHoodieApiConfigured());
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [valueIds, setValueIds] = useState([]);
  const [badgeIds, setBadgeIds] = useState([]);
  const [philanthropyIds, setPhilanthropyIds] = useState([]);
  const [categoryTagIds, setCategoryTagIds] = useState([]);
  const [sortFeatured, setSortFeatured] = useState(false);

  const categories = groupings ?? FALLBACK_CATEGORIES;

  const tagIdToTag = useMemo(() => buildTagIdMap(groupings ?? []), [groupings]);

  const filterState = useMemo(
    () => ({
      search: searchApplied,
      valueIds,
      badgeIds,
      philanthropyIds,
      categoryTagIds,
    }),
    [searchApplied, valueIds, badgeIds, philanthropyIds, categoryTagIds]
  );

  const filteredListings = useMemo(() => {
    const list = applyListingsFilters(allListings, filterState, tagIdToTag);
    if (sortFeatured) return [...list].sort((a, b) => (b.heart_count ?? 0) - (a.heart_count ?? 0));
    return list;
  }, [allListings, filterState, tagIdToTag, sortFeatured]);

  const hasActiveFilters =
    searchApplied.trim() !== "" ||
    valueIds.length > 0 ||
    badgeIds.length > 0 ||
    philanthropyIds.length > 0 ||
    categoryTagIds.length > 0;

  const fetchListings = useCallback((opts = {}) => {
    const { offset = 0, append = false } = opts;
    if (!isHoodieApiConfigured()) return Promise.resolve();
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    return getListings({ limit: LISTINGS_PAGE_SIZE, offset })
      .then((data) => {
        const results = data?.results ?? (Array.isArray(data) ? data : []);
        setAllListings((prev) => (append ? prev.concat(results) : results));
        const next = data?.next;
        setNextOffset(
          next
            ? (() => {
                try {
                  const u = new URL(next);
                  const o = u.searchParams.get("offset");
                  return o != null ? parseInt(o, 10) : null;
                } catch (_) {
                  return null;
                }
              })()
            : null
        );
      })
      .catch((err) => {
        setError(err?.message || "Could not load listings");
        if (!append) setAllListings([]);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    if (!isHoodieApiConfigured()) {
      setLoading(false);
      return;
    }
    getTagGroupings()
      .then((data) => setGroupings(normalizeGroupings(data)))
      .catch(() => setGroupings(null));
  }, []);

  useEffect(() => {
    if (!isHoodieApiConfigured()) return;
    fetchListings({ offset: 0, append: false });
  }, [fetchListings]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchApplied(searchInput);
  };

  const handleClearAll = useCallback(() => {
    setSearchInput("");
    setSearchApplied("");
    setValueIds([]);
    setBadgeIds([]);
    setPhilanthropyIds([]);
    setCategoryTagIds([]);
  }, []);

  const handleClearCategoryFilters = useCallback(() => {
    setCategoryTagIds([]);
  }, []);

  const handleLoadMore = () => {
    if (nextOffset == null || loadingMore) return;
    fetchListings({ offset: nextOffset, append: true });
  };

  return (
    <div className="twentytwentysix-content">
      <div className="twentytwentysix-content__container">
        <MListHeroIntroSection />

        {!isHoodieApiConfigured() && (
          <section className="twentytwentysix-content__section twentytwentysix-content__api-notice" role="status">
            <p className="twentytwentysix-content__copy twentytwentysix-content__api-notice-text">
              <strong>No M List API connected.</strong> To see listings here, set <code>REACT_APP_HOODIE_API_BASE_URL</code> to your Django backend (e.g. <code>https://do-app.internal.themamahood.co</code>), then restart the dev server. Ensure the backend is serving <code>/api/hoodie/listings/</code>.
            </p>
          </section>
        )}

        {error && (
          <div className="twentytwentysix-content__section" role="alert" style={{ padding: "12px", background: "#fde8e8", borderRadius: "8px", color: "#c00" }}>
            {error}
          </div>
        )}

        {isHoodieApiConfigured() && (
          <div className="twentytwentysix-content__mlist-layout">
            <MListSidebar
              groupings={groupings}
              selectedTagIds={categoryTagIds}
              onTagIdsChange={setCategoryTagIds}
              onClearCategoryFilters={handleClearCategoryFilters}
              hasCategorySelection={categoryTagIds.length > 0}
              valueIds={valueIds}
              onValueIdsChange={setValueIds}
              badgeIds={badgeIds}
              onBadgeIdsChange={setBadgeIds}
              philanthropyIds={philanthropyIds}
              onPhilanthropyIdsChange={setPhilanthropyIds}
              onClearAll={handleClearAll}
              hasActiveFilters={hasActiveFilters}
            />

            <main className="twentytwentysix-content__mlist-main">
              <MListFilterBar
                searchInput={searchInput}
                onSearchChange={setSearchInput}
                onSearchSubmit={handleSearchSubmit}
                onClearAll={handleClearAll}
                hasActiveFilters={hasActiveFilters}
              />

              <MListResultsSection
                listings={filteredListings}
                loading={loading}
                loadingMore={loadingMore}
                onLoadMore={handleLoadMore}
                nextOffset={nextOffset}
                categories={categories}
                sortFeatured={sortFeatured}
                onSortFeaturedChange={setSortFeatured}
                hasDataFromApi={allListings.length > 0}
                tagIdToTag={tagIdToTag}
              />
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
